import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useMutation, useApolloClient } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import gql from 'graphql-tag';
import { FormattedMessage, useIntl } from 'react-intl';
import { getDealRoomBrandedName } from '../../../utils/locale';
import WizardStepper from './DealWizardComponents/WizardStepper';
import DealTypeSelection from './DealWizardSteps/DealTypeSelection';
import DealInformationForm from './DealWizardSteps/DealInformationForm';
import FinancialInformation from './DealWizardSteps/FinancialInformation';
import AdditionalDetails from './DealWizardSteps/AdditionalDetails';
import MatchPreferences from './DealWizardSteps/MatchPreferences';
import DealQuestions from './DealWizardSteps/DealQuestions';
import ReviewAndPublish from './DealWizardSteps/ReviewAndPublish';
import WizardNavigation from './DealWizardComponents/WizardNavigation';
import MethodSelection from './DealWizardSteps/MethodSelection';
import { useAgent } from '../AgentSystem/AgentProvider';
import { currencySymbol } from '../../../utils/formatters';

const CREATE_DEAL = gql`
  mutation CreateDeal(
    $inventory_user_id: ID,
    $headline: String,
    $deal_type: String,
    $invitation_deadline: String
  ) {
    createDeal(
      inventory_user_id: $inventory_user_id,
      headline: $headline,
      deal_type: $deal_type,
      invitation_deadline: $invitation_deadline
    ) {
      id
      deal_name
      deal_type
      headline
      deal_state
    }
  }
`;

const POPULATE_DEMO_TEASER = gql`
  mutation PopulateDemoTeaserContent {
    populateDemoTeaserContent {
      success
      errors
      teaserContent {
        headline
        subHeadline
        summaryOfBusiness
        assetsIncluded
        intangibleAssetsIncluded
        growthOpportunities
        customerDemographicsComments
        productsAndServicesComments
        idealCandidate
        listPrice
        question1
        question2
        question3
        question4
        question5
      }
    }
  }
`;

const PUBLISH_DEAL = gql`
  mutation PublishDeal(
    $id: ID!,
    $headline: String,
    $sub_headline: String,
    $summary_of_business: String,
    $ideal_candidate: String,
    $growth_opportunities: String,
    $assets_included: String,
    $intangible_assets_included: String,
    $customer_demographics_comments: String,
    $products_and_services_comments: String,
    $invitation_deadline: String,
    $deal_type: String,
    $list_price: Int,
    $question_1: String,
    $question_2: String,
    $question_3: String,
    $question_4: String,
    $question_5: String,
    $invite_buyer_id: ID
  ) {
    publishDeal(
      id: $id,
      headline: $headline,
      sub_headline: $sub_headline,
      summary_of_business: $summary_of_business,
      ideal_candidate: $ideal_candidate,
      growth_opportunities: $growth_opportunities,
      assets_included: $assets_included,
      intangible_assets_included: $intangible_assets_included,
      customer_demographics_comments: $customer_demographics_comments,
      products_and_services_comments: $products_and_services_comments,
      invitation_deadline: $invitation_deadline,
      deal_type: $deal_type,
      list_price: $list_price,
      question_1: $question_1,
      question_2: $question_2,
      question_3: $question_3,
      question_4: $question_4,
      question_5: $question_5,
      invite_buyer_id: $invite_buyer_id
    ) {
      success
      errors
      async
      dealName
      jobState
      message
      runId
    }
  }
`;

// Wizard autosave. Deliberately does NOT touch Deal — a draft Deal row would fire the
// creation mailers, park a pipeline card, index into OpenSearch, and (because this
// wizard is also the edit surface for a published deal) push keystrokes to the live
// marketplace listing. See docs/plans/2026-09-16-fix-deal-wizard-draft-autosave-plan.md
const SAVE_DEAL_DRAFT = gql`
  mutation SaveDealDraft($form_data: JSON, $current_step: Int) {
    save_deal_draft(form_data: $form_data, current_step: $current_step) {
      inventory_user {
        id
        deal_draft_step
      }
      errors
    }
  }
`;

const DISCARD_DEAL_DRAFT = gql`
  mutation DiscardDealDraft {
    discard_deal_draft {
      success
      errors
    }
  }
`;

const DEAL_PUBLISH_STATUS = gql`
  query DealPublishStatus($dealId: ID!) {
    dealPublishStatus(dealId: $dealId) {
      status
      message
      matchCount
      runId
      updatedAt
    }
  }
`;

const DealWizard = ({ data }) => {
  // RJ Seller Visibility — the buyer the seller was trying to invite when they
  // discovered they had no Meeting Room, arriving as
  // `/succession/deals/wizard?invite_buyer_id=…`. Carried through to `publishDeal`
  // so the invite completes instead of stranding the seller on the deals index.
  //
  // Captured ONCE via a useState initializer and mirrored into sessionStorage:
  // this wizard is multi-step and navigates as the seller advances, so reading
  // `window.location.search` at publish time would find the query string gone.
  const [inviteBuyerId] = useState(() => {
    if (typeof window === 'undefined') return data?.invite_buyer_id || null;

    const key = 'rj_invite_buyer_id';
    const fromUrl = new URLSearchParams(window.location.search).get('invite_buyer_id');
    const resolved = fromUrl || data?.invite_buyer_id || null;

    try {
      if (resolved) {
        window.sessionStorage.setItem(key, resolved);
        return resolved;
      }
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return resolved;
    }
  });

  // Don't let it leak into a later, unrelated run of the wizard.
  const clearInviteBuyerId = () => {
    try {
      if (typeof window !== 'undefined') window.sessionStorage.removeItem('rj_invite_buyer_id');
    } catch (e) { /* sessionStorage unavailable — nothing to clear */ }
  };

  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [createDeal, { loading: createLoading }] = useMutation(CREATE_DEAL);
  const [publishDeal, { loading: publishLoading }] = useMutation(PUBLISH_DEAL);
  const [populateDemoTeaser] = useMutation(POPULATE_DEMO_TEASER);
  const [saveDealDraft] = useMutation(SAVE_DEAL_DRAFT);
  const [discardDealDraft] = useMutation(DISCARD_DEAL_DRAFT);
  const mutationLoading = createLoading || publishLoading;
  const publishPollRef = useRef(null);
  const [publishOperationInProgress, setPublishOperationInProgress] = useState(null); // { message }
  const [activePublishRunId, setActivePublishRunId] = useState(null);

  // Track if demo teaser content has been loaded
  const [demoTeaserLoaded, setDemoTeaserLoaded] = useState(false);

  // Agent system for AI-guided path
  const { spawnWidget } = useAgent();

  // Check for edit mode from URL params
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get('mode') === 'edit';

  const currentUser = data?.getCurrentUser;
  const company = currentUser?.company;

  // Check if AI content creation copilot feature is enabled
  const hasAIContentCopilot = company?.features?.includes('deal_teaser_content_creation_copilot');

  // Check if demo_secret_menu feature is enabled
  const hasDemoSecretMenu = currentUser?.features?.includes('demo_secret_menu') || company?.features?.includes('demo_secret_menu');

  // Declared here, above the effects, because both are read inside them AND listed in
  // their dependency arrays. Declared below, the dep array evaluates during render while
  // the const is still in its temporal dead zone — and .babelrc sets no targets, so
  // `const` is transpiled to `var` and the TDZ error never throws: the dependency
  // silently reads `undefined` on every render instead.
  const existingDeal = currentUser?.inventory_user?.deals?.length > 0
    ? currentUser.inventory_user.deals[0]
    : null;

  // Unfinished wizard work, saved before any Deal exists. A published deal being edited
  // takes precedence: the draft is for creation, and autosaving over a live listing is
  // the regression this design exists to avoid.
  //
  // deal_draft_data defaults to {} for every inventory_user, so emptiness — not
  // presence — is what distinguishes "has a draft" from "has never started one".
  const draftData = existingDeal ? null : currentUser?.inventory_user?.deal_draft_data;
  const existingDraft = draftData && Object.keys(draftData).length > 0
    ? { form_data: draftData, current_step: currentUser?.inventory_user?.deal_draft_step || 1 }
    : null;

  // Track whether user has selected a method (AI or Manual)
  // null = not yet determined (data loading), true = method selected, false = needs to select
  const [methodSelected, setMethodSelected] = useState(null);

  useEffect(() => {
    return () => {
      if (publishPollRef.current) {
        clearInterval(publishPollRef.current);
      }
    };
  }, []);

  // Once data loads, determine if we need to show method selection
  useEffect(() => {
    if (company && methodSelected === null) {
      // If feature is enabled, show method selection; otherwise skip directly to wizard
      const shouldSkipMethodSelection = !hasAIContentCopilot;
      console.log('DealWizard - Feature check:', {
        hasAIContentCopilot,
        shouldSkipMethodSelection,
        features: company?.features
      });
      setMethodSelected(shouldSkipMethodSelection);
    }
  }, [company, hasAIContentCopilot, methodSelected]);

  // Pre-populate deal information with demo content when demo_secret_menu is enabled
  useEffect(() => {
    const loadDemoTeaser = async () => {
      // Only load demo teaser if:
      // 1. demo_secret_menu is enabled
      // 2. Haven't already loaded demo teaser
      // 3. No existing deal with content (headline indicates deal info was filled)
      // A resumed draft is the advisor's own work. Without this, demo content overwrites
      // it on every mount and the autosave then persists the demo copy over their edits —
      // so the wizard appears to "forget" everything they typed. Checked before the
      // headline test because a draft may legitimately have no headline yet.
      const existingHasContent = existingDraft != null
        || (existingDeal?.headline && existingDeal.headline.trim() !== '');

      if (hasDemoSecretMenu && !demoTeaserLoaded && !existingHasContent) {
        console.log('DealWizard - Loading demo teaser content for demo_secret_menu');
        try {
          const { data: teaserData } = await populateDemoTeaser();
          if (teaserData?.populateDemoTeaserContent?.success && teaserData.populateDemoTeaserContent.teaserContent) {
            const content = teaserData.populateDemoTeaserContent.teaserContent;
            console.log('DealWizard - Demo teaser content loaded:', content);
            setFormData(prev => ({
              ...prev,
              headline: content.headline || prev.headline,
              sub_headline: content.subHeadline || prev.sub_headline,
              summary_of_business: content.summaryOfBusiness || prev.summary_of_business,
              assets_included: content.assetsIncluded || prev.assets_included,
              intangible_assets_included: content.intangibleAssetsIncluded || prev.intangible_assets_included,
              growth_opportunities: content.growthOpportunities || prev.growth_opportunities,
              customer_demographics_comments: content.customerDemographicsComments || prev.customer_demographics_comments,
              products_and_services_comments: content.productsAndServicesComments || prev.products_and_services_comments,
              ideal_candidate: content.idealCandidate || prev.ideal_candidate,
              list_price: content.listPrice || prev.list_price,
              list_price_type: content.listPrice ? 'custom' : prev.list_price_type,
              question_1: content.question1 || prev.question_1,
              question_2: content.question2 || prev.question_2,
              question_3: content.question3 || prev.question_3,
              question_4: content.question4 || prev.question_4,
              question_5: content.question5 || prev.question_5,
            }));
          }
        } catch (error) {
          console.error('DealWizard - Error loading demo teaser:', error);
        }
        setDemoTeaserLoaded(true);
      }
    };

    loadDemoTeaser();
  }, [hasDemoSecretMenu, demoTeaserLoaded, existingDeal, existingDraft, populateDemoTeaser]);

  // Debug logging
  console.log('DealWizard - hasAIContentCopilot:', hasAIContentCopilot);
  console.log('DealWizard - methodSelected:', methodSelected);
  console.log('DealWizard - Company features:', company?.features);
  console.log('DealWizard - Inventory user:', currentUser?.inventory_user);
  console.log('DealWizard - All deals:', currentUser?.inventory_user?.deals);
  
  // existingDeal / existingDraft are declared above, before the effects that read them.

  console.log('DealWizard - Existing deal found:', existingDeal);
  console.log('DealWizard - All deal states:', currentUser?.inventory_user?.deals?.map(d => ({id: d.id, state: d.deal_state, name: d.deal_name})));
  
  // Initialize form data with existing deal if available
  const getInitialFormData = () => {
    if (existingDeal) {
      console.log('DealWizard - Populating form with existing deal:', existingDeal);
      const initialData = {
        id: existingDeal.id,
        deal_type: existingDeal.deal_type || 'succession',
        headline: existingDeal.headline || '',
        sub_headline: existingDeal.sub_headline || '',
        summary_of_business: existingDeal.summary_of_business || '',
        ideal_candidate: existingDeal.ideal_candidate || '',
        list_price: existingDeal.list_price || null,
        list_price_type: existingDeal.list_price_type || (existingDeal.list_price ? 'custom' : 'tbd'),
        invitation_deadline: existingDeal.invitation_deadline ? 
          new Date(existingDeal.invitation_deadline).toISOString().split('T')[0] :
          new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
        deal_state: existingDeal.deal_state || 'new_deal', // Fixed: use 'new_deal' not 'draft'
        growth_opportunities: existingDeal.growth_opportunities || '',
        assets_included: existingDeal.assets_included || '',
        intangible_assets_included: existingDeal.intangible_assets_included || '',
        customer_demographics_comments: existingDeal.customer_demographics_comments || '',
        products_and_services_comments: existingDeal.products_and_services_comments || '',
        question_1: existingDeal.question_1 || '',
        question_2: existingDeal.question_2 || '',
        question_3: existingDeal.question_3 || '',
        question_4: existingDeal.question_4 || '',
        question_5: existingDeal.question_5 || ''
      };
      console.log('DealWizard - Initial form data:', initialData);
      return initialData;
    }
    
    const blank = {
      deal_type: 'succession',
      headline: '',
      sub_headline: '',
      summary_of_business: '',
      ideal_candidate: '',
      list_price: null,
      list_price_type: 'tbd',
      invitation_deadline: new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
      deal_state: 'new_deal',
      growth_opportunities: '',
      assets_included: '',
      intangible_assets_included: '',
      customer_demographics_comments: '',
      products_and_services_comments: '',
      question_1: '',
      question_2: '',
      question_3: '',
      question_4: '',
      question_5: ''
    };

    // No published deal to edit, but unfinished wizard work on file — resume it.
    // Merged over the blank defaults rather than replacing them so a draft saved
    // before a field existed still yields a complete form.
    return existingDraft?.form_data
      ? { ...blank, ...existingDraft.form_data }
      : blank;
  };
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(existingDraft?.current_step || 1);
  const [formData, setFormData] = useState(getInitialFormData);
  const [errors, setErrors] = useState({});
  const [isDraft, setIsDraft] = useState(true);

  // Autosave state. 'unsaved' is load-bearing: a silent autosave that fails on session
  // expiry or offline is worse than no autosave, because the user stops being careful.
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const autosaveEnabled = !existingDeal; // never autosave over a published listing
  const dirtyRef = useRef(false);
  const skipFirstSaveRef = useRef(true);

  const totalSteps = 5; // Consolidated form with deal info, match preferences, questions

  // Step validation
  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.deal_type) {
          newErrors.deal_type = intl.formatMessage({ id: 'succession_deals.wizard.validation.deal_type_required' });
        }
        break;
      case 2:
        // Check all required fields have content
        if (!formData.headline?.trim()) {
          newErrors.headline = intl.formatMessage({ id: 'succession_deals.wizard.validation.headline_required' });
        }
        // Only require sub_headline if the company setting allows it to be shown
        if (!company?.teaser_hide_business_overview_question && !formData.sub_headline?.trim()) {
          newErrors.sub_headline = intl.formatMessage({ id: 'succession_deals.wizard.validation.business_overview_required' });
        }
        // All other fields are now required to have at least some content
        if (!formData.summary_of_business?.trim()) {
          newErrors.summary_of_business = intl.formatMessage({ id: 'succession_deals.wizard.validation.business_summary_required' });
        }
        if (!formData.ideal_candidate?.trim()) {
          newErrors.ideal_candidate = intl.formatMessage({ id: 'succession_deals.wizard.validation.ideal_candidate_required' });
        }
        if (!formData.growth_opportunities?.trim()) {
          newErrors.growth_opportunities = intl.formatMessage({ id: 'succession_deals.wizard.validation.growth_opportunities_required' });
        }
        if (!formData.assets_included?.trim()) {
          newErrors.assets_included = intl.formatMessage({ id: 'succession_deals.wizard.validation.assets_included_required' });
        }
        if (!formData.intangible_assets_included?.trim()) {
          newErrors.intangible_assets_included = intl.formatMessage({ id: 'succession_deals.wizard.validation.intangible_assets_required' });
        }
        if (!formData.customer_demographics_comments?.trim()) {
          newErrors.customer_demographics_comments = intl.formatMessage({ id: 'succession_deals.wizard.validation.client_demographics_required' });
        }
        if (!formData.products_and_services_comments?.trim()) {
          newErrors.products_and_services_comments = intl.formatMessage({ id: 'succession_deals.wizard.validation.products_services_required' });
        }
        break;
      case 3:
        // Match preferences validation
        if (!formData.match_preferences_complete) {
          // Fallback validation: check if the basic preferences are filled out
          const hasProximityMax = formData.proximity_max && formData.proximity_max > 0;
          
          // Check required questions based on company settings
          const requiredFields = ['practice_similarity', 'practice_structure', 'servicing', 'length_of_service', 'avg_age_of_client'];
          
          // Add AUM if not P&C business type
          if (company?.business_type !== 'p_and_c') {
            requiredFields.push('aum');
          }
          
          // Add affiliation questions if they should be shown
          if (company?.has_affiliation) {
            requiredFields.push('affiliation');
          }
          if (company?.allow_user_affiliations) {
            requiredFields.push('affiliations');
          }
          
          const hasBasicPreferences = requiredFields.every(field => 
            formData[field] !== null && formData[field] !== undefined
          );
          
          // If basic preferences aren't filled, show validation error
          if (!hasProximityMax || !hasBasicPreferences) {
            newErrors.match_preferences = intl.formatMessage({ id: 'succession_deals.wizard.validation.match_preferences_required' });
          }
        }
        break;
      case 4:
        // Deal questions validation - require at least one question and all added questions must be complete
        if (!formData.questions_validation?.isValid) {
          // Fallback validation: check if at least one question is filled out
          const questions = [
            formData.question_1,
            formData.question_2,
            formData.question_3,
            formData.question_4,
            formData.question_5
          ];
          
          const filledQuestions = questions.filter(q => q && q.trim() !== '');
          const hasAtLeastOneQuestion = filledQuestions.length >= 1;
          
          // If no questions are filled, show validation error
          if (!hasAtLeastOneQuestion) {
            newErrors.questions = intl.formatMessage({ id: 'succession_deals.wizard.validation.questions_required' });
          }
        }
        break;
      case 5:
        // Final validation
        if (!formData.headline?.trim()) {
          newErrors.headline = intl.formatMessage({ id: 'succession_deals.wizard.validation.headline_required' });
        }
        if (!company?.teaser_hide_business_overview_question && !formData.sub_headline?.trim()) {
          newErrors.sub_headline = intl.formatMessage({ id: 'succession_deals.wizard.validation.business_overview_required' });
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Scroll to top whenever the step changes
  useEffect(() => {
    // The succession layout uses a scrollable container with class 'main-content-mobile'
    // We need to scroll that element, not the window
    const scrollContainer = document.querySelector('.main-content-mobile');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      // Fallback to window scroll if container not found
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [currentStep]);

  // Persist wizard progress. Returns the mutation promise so callers that must not
  // race it (step advance, publish) can await.
  //
  // Sends the whole formData rather than a diff: the server merges per key, and a
  // 19-key object is not worth the bookkeeping of tracking which keys changed.
  const persistDraft = (step = currentStep) => {
    if (!autosaveEnabled) return Promise.resolve();

    setSaveState('saving');
    return saveDealDraft({ variables: { form_data: formData, current_step: step } })
      .then((response) => {
        const errs = response?.data?.save_deal_draft?.errors || [];
        if (errs.length > 0) {
          setSaveState('error');
          return;
        }
        dirtyRef.current = false;
        setSaveState('saved');
        if (window.ahoy) window.ahoy.track('deal_wizard_draft_saved', { step });
      })
      .catch(() => {
        // Session expiry, offline, 500. Surfaced, never swallowed — see saveState.
        setSaveState('error');
      });
  };

  // Debounced autosave. 1s matches the existing debounce in this wizard
  // (MatchPreferences.jsx:378-385), so the two saves feel like one behaviour.
  useEffect(() => {
    if (!autosaveEnabled) return undefined;
    // Don't write on mount — that would create a draft for anyone who merely opened
    // the wizard, which is how the legacy flow ended up with empty rows.
    if (skipFirstSaveRef.current) {
      skipFirstSaveRef.current = false;
      return undefined;
    }

    dirtyRef.current = true;
    const timeoutId = setTimeout(() => { persistDraft(); }, 1000);
    return () => clearTimeout(timeoutId);
  }, [formData]);

  // Don't let someone close the tab believing a failed or in-flight save landed.
  useEffect(() => {
    const warnIfDirty = (e) => {
      if (!dirtyRef.current && saveState !== 'error') return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warnIfDirty);
    return () => window.removeEventListener('beforeunload', warnIfDirty);
  }, [saveState]);

  useEffect(() => {
    if (existingDraft && window.ahoy) {
      window.ahoy.track('deal_wizard_draft_resumed', { step: existingDraft.current_step });
    }
  }, []);

  const handleDiscardDraft = async () => {
    await discardDealDraft().catch(() => {});
    if (window.ahoy) window.ahoy.track('deal_wizard_draft_discarded', { step: currentStep });
    dirtyRef.current = false;
    window.location.reload();
  };

  // Navigation handlers
  const handleNext = () => {
    console.log('DealWizard - handleNext called, current step:', currentStep);
    console.log('DealWizard - Current form data:', formData);
    console.log('DealWizard - Company settings:', company);

    const isValid = validateStep(currentStep);
    console.log('DealWizard - Step validation result:', isValid);
    console.log('DealWizard - Current errors:', errors);

    if (isValid) {
      if (currentStep < totalSteps) {
        console.log('DealWizard - Moving to next step:', currentStep + 1);
        const nextStep = currentStep + 1;
        // Persist before advancing, so the resume cursor can never point at a step
        // whose content did not save (ReadinessAssessmentFlow.jsx:394-414 does the
        // same, for the same reason).
        persistDraft(nextStep);
        setCurrentStep(nextStep);
      }
    } else {
      console.log('DealWizard - Validation failed, staying on current step');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      // Clear errors when going back - they shouldn't persist from future steps
      setErrors({});
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step) => {
    // Allow navigation to completed steps or the next immediate step
    if (step <= currentStep || step === currentStep + 1) {
      // Clear errors when navigating to different steps
      setErrors({});
      setCurrentStep(step);
    }
  };

  // Method selection handlers (AI vs Manual)
  const handleSelectAI = () => {
    // Check if we have an existing deal with teaser content - if so, skip to review mode
    let existingContent = null;
    if (existingDeal && existingDeal.headline && existingDeal.summary_of_business) {
      // Convert deal fields to teaser format for editing
      existingContent = {
        deal_name: existingDeal.deal_name || '',
        headline: existingDeal.headline || '',
        business_summary: existingDeal.summary_of_business || '',
        ideal_candidate: existingDeal.ideal_candidate || '',
        growth_opportunities: existingDeal.growth_opportunities || '',
        assets_included: existingDeal.assets_included || '',
        intangible_assets: existingDeal.intangible_assets_included || '',
        client_demographics: existingDeal.customer_demographics_comments || '',
        products_and_services: existingDeal.products_and_services_comments || '',
        asking_price: existingDeal.list_price ? `${currencySymbol()}${existingDeal.list_price.toLocaleString()}` : '',
        deal_type: existingDeal.deal_type || 'succession',
        screening_questions: [
          existingDeal.question_1,
          existingDeal.question_2,
          existingDeal.question_3,
          existingDeal.question_4,
          existingDeal.question_5,
        ].filter(q => q && q.trim() !== ''),
      };
    }

    // Spawn the ContentCreationCopilot widget for AI-guided teaser creation
    spawnWidget('content_creation_copilot', {
      dealId: existingDeal?.id,
      inventoryUserId: currentUser?.inventory_user?.id,
      dealRoomBrandedName: getDealRoomBrandedName(company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' }),
      company: company, // Pass company with features for deal type selection
      currentUser: currentUser, // Pass currentUser for match preferences modal
      existingContent: existingContent, // Pass existing teaser for edit mode
    }, 'modal');
  };

  const handleSelectManual = () => {
    // User chose manual path - proceed to normal wizard flow
    setMethodSelected(true);
  };

  // Track if we've triggered edit mode to prevent multiple triggers
  const [editModeTriggered, setEditModeTriggered] = useState(false);

  // Auto-trigger AI mode when coming from "Edit Teaser" button with mode=edit
  useEffect(() => {
    if (
      isEditMode &&
      !editModeTriggered &&
      hasAIContentCopilot &&
      existingDeal &&
      existingDeal.headline &&
      existingDeal.summary_of_business &&
      company
    ) {
      console.log('DealWizard - Edit mode detected, auto-triggering AI flow');
      setEditModeTriggered(true);
      handleSelectAI();
    }
  }, [isEditMode, editModeTriggered, hasAIContentCopilot, existingDeal, company]);

  // Form data update handler
  const updateFormData = (updates) => {
    console.log('DealWizard - updateFormData called with:', updates);
    setFormData(prev => {
      const newData = { ...prev, ...updates };
      console.log('DealWizard - Updated form data:', newData);
      return newData;
    });
    
    // Clear related errors
    const clearedErrors = { ...errors };
    Object.keys(updates).forEach(key => {
      if (clearedErrors[key]) {
        delete clearedErrors[key];
      }
    });
    setErrors(clearedErrors);
  };

  const readIsDarkTheme = () => {
    const savedTheme = localStorage.getItem('succession-theme') || 'light';
    return savedTheme === 'dark' || document.documentElement.classList.contains('succession-dark');
  };

  const isDarkTheme = readIsDarkTheme();
  const primaryColor = company?.primary_color || '#5489B6';
  const themeColors = {
    bgPrimary: isDarkTheme ? '#0f172a' : '#ffffff',
    bgSecondary: isDarkTheme ? '#1e293b' : '#f8fafc',
    textPrimary: isDarkTheme ? '#f8fafc' : '#0f172a',
    textSecondary: isDarkTheme ? '#cbd5e1' : '#475569',
    textMuted: isDarkTheme ? '#94a3b8' : '#64748b',
    border: isDarkTheme ? '#64748b' : '#e5e7eb',
    primary: primaryColor,
  };

  const startPublishPolling = ({ dealId, runId, dealName }) => {
    if (publishPollRef.current) {
      clearInterval(publishPollRef.current);
    }

    publishPollRef.current = setInterval(async () => {
      try {
        const { data: pollData } = await apolloClient.query({
          query: DEAL_PUBLISH_STATUS,
          variables: { dealId },
          fetchPolicy: 'network-only',
        });

        const status = pollData?.dealPublishStatus;
        if (!status) return;
        if (runId && status.runId && status.runId !== runId) return;

        if (status.status === 'completed') {
          clearInterval(publishPollRef.current);
          publishPollRef.current = null;
          setPublishOperationInProgress({ message: intl.formatMessage({ id: 'succession_deals.wizard.publish.success_redirecting' }) });
          setTimeout(() => history.push(`/succession/deals/${dealName}/teaser`), 800);
        } else if (status.status === 'failed') {
          clearInterval(publishPollRef.current);
          publishPollRef.current = null;
          setActivePublishRunId(null);
          setPublishOperationInProgress(null);
          setErrors({ submit: status.message || intl.formatMessage({ id: 'succession_deals.wizard.errors.publish_failed' }) });
        } else {
          setPublishOperationInProgress({
            message: status.message || intl.formatMessage({ id: 'succession_deals.wizard.publish.in_progress' }),
          });
        }
      } catch (_pollError) {
        // transient polling errors should retry on next interval
      }
    }, 1500);
  };

  // Publish deal
  const handlePublish = async () => {
    if (activePublishRunId && publishOperationInProgress) {
      return;
    }

    // Validate all steps
    let allValid = true;
    for (let step = 1; step <= totalSteps; step++) {
      if (!validateStep(step)) {
        allValid = false;
        break;
      }
    }

    if (!allValid) {
      return;
    }

    try {
      let dealResult = existingDeal;

      if (!dealResult) {
        const { data: mutationData } = await createDeal({
          variables: {
            inventory_user_id: currentUser.inventory_user.id,
            headline: formData.headline,
            deal_type: formData.deal_type,
            invitation_deadline: formData.invitation_deadline,
          },
        });
        dealResult = mutationData?.createDeal;
      }

      if (!dealResult) {
        throw new Error('Could not create deal before publishing');
      }

      const { data: publishData } = await publishDeal({
        variables: {
          id: dealResult.id,
          headline: formData.headline,
          sub_headline: formData.sub_headline,
          summary_of_business: formData.summary_of_business,
          ideal_candidate: formData.ideal_candidate,
          growth_opportunities: formData.growth_opportunities,
          assets_included: formData.assets_included,
          intangible_assets_included: formData.intangible_assets_included,
          customer_demographics_comments: formData.customer_demographics_comments,
          products_and_services_comments: formData.products_and_services_comments,
          invitation_deadline: formData.invitation_deadline,
          deal_type: formData.deal_type,
          list_price: formData.list_price ? parseInt(formData.list_price, 10) : null,
          question_1: formData.question_1,
          question_2: formData.question_2,
          question_3: formData.question_3,
          question_4: formData.question_4,
          question_5: formData.question_5,
          // RJ Seller Visibility — present when the seller arrived here from
          // "Invite to Meeting Room" with no room yet. The server completes the
          // invite as part of publish, BEFORE the matching sweep is enqueued, so
          // the buyer gets the invitation email and not a suggested-match one.
          invite_buyer_id: inviteBuyerId || null,
        },
      });
      clearInviteBuyerId();

      const result = publishData?.publishDeal;
      if (!result?.success) {
        throw new Error(result?.message || result?.errors?.join(', ') || 'Publish failed');
      }

      // The draft has served its purpose — the Deal is the record now. Cleared only
      // after a confirmed success, so a failed publish leaves the work recoverable.
      dirtyRef.current = false;
      discardDealDraft().catch(() => {});

      const runId = result.runId || null;
      setActivePublishRunId(runId);
      setPublishOperationInProgress({
        message: result.message || 'Publishing Deal Room... This may take a moment.',
      });
      startPublishPolling({
        dealId: dealResult.id,
        runId,
        dealName: result.dealName || dealResult.deal_name,
      });
    } catch (error) {
      console.error('Error publishing deal:', error);
      setActivePublishRunId(null);
      setPublishOperationInProgress(null);
      setErrors({ submit: intl.formatMessage({ id: 'succession_deals.wizard.errors.publish_failed' }) });
    }
  };

  // Render step content
  const renderStepContent = () => {
    const stepProps = {
      formData,
      updateFormData,
      errors,
      currentUser,
      company
    };

    switch (currentStep) {
      case 1:
        return <DealTypeSelection {...stepProps} />;
      case 2:
        return <DealInformationForm {...stepProps} />;
      case 3:
        return <MatchPreferences {...stepProps} />;
      case 4:
        return <DealQuestions {...stepProps} />;
      case 5:
        return <ReviewAndPublish {...stepProps} />;
      default:
        return <DealTypeSelection {...stepProps} />;
    }
  };

  const dealRoomBrandedName = getDealRoomBrandedName(company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' });

  // Show loading state while determining if method selection is needed
  if (methodSelected === null) {
    return (
      <div className="bg-succession-bg-secondary min-h-screen flex items-center justify-center">
        <div className="text-succession-text-secondary">Loading...</div>
      </div>
    );
  }

  // Show method selection if feature is enabled and user hasn't selected yet
  if (!methodSelected) {
    return (
      <div className="bg-succession-bg-secondary min-h-screen">
        <MethodSelection
          onSelectAI={handleSelectAI}
          onSelectManual={handleSelectManual}
          dealRoomBrandedName={dealRoomBrandedName}
        />
      </div>
    );
  }

  return (
    <div className="bg-succession-bg-secondary min-h-screen">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-12 md:mb-16">
          {/* Autosave state. Rendered for draft creation only — editing a published
              deal does not autosave, and saying "Saved" there would be a lie. */}
          {autosaveEnabled && saveState !== 'idle' && (
            <div className="mb-4 flex items-center gap-3">
              {saveState === 'error' ? (
                <span className="text-sm text-red-600" role="alert">
                  <FormattedMessage id="succession_deals.wizard.autosave.error" />
                </span>
              ) : (
                <span className="text-sm text-succession-text-secondary" aria-live="polite">
                  <FormattedMessage
                    id={saveState === 'saving'
                      ? 'succession_deals.wizard.autosave.saving'
                      : 'succession_deals.wizard.autosave.saved'}
                  />
                </span>
              )}
              {saveState === 'error' && (
                <button
                  type="button"
                  onClick={() => persistDraft()}
                  className="text-sm underline text-succession-text-primary">
                  <FormattedMessage id="succession_deals.wizard.autosave.retry" />
                </button>
              )}
              {existingDraft && (
                <button
                  type="button"
                  onClick={handleDiscardDraft}
                  className="text-sm underline text-succession-text-secondary ml-auto">
                  <FormattedMessage id="succession_deals.wizard.autosave.discard" />
                </button>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-succession-text-primary">
                {existingDeal ? (
                  <FormattedMessage
                    id="succession_deals.wizard.header.edit_title"
                    values={{ dealRoomName: dealRoomBrandedName }}
                  />
                ) : (
                  <FormattedMessage
                    id="succession_deals.wizard.header.create_title"
                    values={{ dealRoomName: dealRoomBrandedName }}
                  />
                )}
              </h1>
              <p className="text-sm text-succession-text-secondary mt-1">
                {existingDeal ? (
                  <FormattedMessage
                    id="succession_deals.wizard.header.edit_description"
                    values={{ dealTitle: existingDeal.headline || intl.formatMessage({ id: 'succession_deals.wizard.header.untitled_deal' }) }}
                  />
                ) : (
                  <FormattedMessage
                    id="succession_deals.wizard.header.create_description"
                    values={{ dealRoomName: dealRoomBrandedName.toLowerCase() }}
                  />
                )}
              </p>
            </div>
          </div>

          {/* Progress Stepper */}
          <WizardStepper
            currentStep={currentStep}
            totalSteps={totalSteps}
            onStepClick={handleStepClick}
          />
        </div>

        <div className="bg-succession-bg-primary shadow-lg rounded-xl overflow-hidden border border-succession-border">
          {/* Step Content */}
          <div className="px-8 py-10">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="px-8 py-6 bg-succession-bg-secondary border-t border-succession-border">
            <WizardNavigation
              currentStep={currentStep}
              totalSteps={totalSteps}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onPublish={handlePublish}
              loading={mutationLoading || !!publishOperationInProgress}
              canProceed={(() => {
                // Only check for errors relevant to the current step
                switch (currentStep) {
                  case 1:
                    return !errors.deal_type;
                  case 2:
                    return !errors.headline && !errors.sub_headline && !errors.summary_of_business &&
                           !errors.ideal_candidate && !errors.growth_opportunities && !errors.assets_included &&
                           !errors.intangible_assets_included && !errors.customer_demographics_comments &&
                           !errors.products_and_services_comments;
                  case 3:
                    return !errors.match_preferences;
                  case 4:
                    return !errors.questions;
                  case 5:
                    return true; // Final step is optional for navigation
                  default:
                    return true;
                }
              })()}
            />
          </div>
        </div>
      </div>

      {publishOperationInProgress && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2147483647,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(2px)',
          }}
        >
          <div
            style={{
              backgroundColor: themeColors.bgPrimary,
              border: `1px solid ${themeColors.border}`,
              borderRadius: '12px', padding: '32px 40px',
              textAlign: 'center', maxWidth: '400px', width: '90%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
              <svg
                style={{ animation: 'demo-spin 1s linear infinite', width: '40px', height: '40px', color: themeColors.primary }}
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              >
                <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <p className="text-sm font-medium" style={{ color: themeColors.textPrimary, marginBottom: '4px' }}>
              {publishOperationInProgress.message}
            </p>
            <p className="text-xs" style={{ color: themeColors.textMuted }}>
              {intl.formatMessage({ id: 'succession_deals.wizard.publish.wait_message' })}
            </p>
          </div>
          <style>{`@keyframes demo-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>,
        document.body
      )}
    </div>
  );
};

DealWizard.propTypes = {
  data: PropTypes.object.isRequired
};

export default DealWizard; 